import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { toysApi } from "@/lib/api";
import { ArrowLeft, Clock, Calendar, Palette, Sparkle, Heart } from "lucide-react";

const ToyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const [toy, setToy] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    toysApi.get(id).then(setToy).catch(() => setToy(null));
  }, [id]);

  if (!toy) {
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <p className="font-body text-muted-foreground">{t.common.loading}</p>
      </div>
    );
  }

  const species = lang === "fr" ? toy.species_fr : toy.species_en;
  const description = lang === "fr" ? toy.description_fr : toy.description_en;
  const personality = lang === "fr" ? toy.personality_fr : toy.personality_en;
  const placeholder = lang === "en" ? "/coming-soon-en.svg" : "/coming-soon.svg";
  const swap = (src) =>
    src && (src.endsWith("/coming-soon.svg") || src.endsWith("/coming-soon-en.svg")) ? placeholder : src;
  const gallery = (toy.gallery?.length ? toy.gallery : [placeholder]).map(swap);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 md:py-16" data-testid="toy-detail-page">
      <Link
        to="/catalogue"
        className="inline-flex items-center gap-2 text-sm text-ink/70 hover:text-terracotta transition-colors mb-10"
        data-testid="back-to-catalog"
      >
        <ArrowLeft size={14} strokeWidth={1.5} />
        {t.common.backToCatalog}
      </Link>

      <div className="grid md:grid-cols-5 gap-10 lg:gap-16">
        {/* Gallery */}
        <div className="md:col-span-3">
          <div className="rounded-2xl overflow-hidden border border-border bg-cream/30 aspect-square">
            <img
              src={gallery[activeIdx]}
              alt={toy.name}
              data-testid="toy-main-image"
              className="w-full h-full object-cover"
            />
          </div>
          {gallery.length > 1 && (
            <div className="grid grid-cols-5 gap-3 mt-4">
              {gallery.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIdx(i)}
                  data-testid={`thumb-${i}`}
                  className={`aspect-square rounded-xl overflow-hidden border transition-all ${
                    i === activeIdx ? "border-terracotta ring-2 ring-terracotta/30" : "border-border"
                  }`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="md:col-span-2">
          <p className="font-script text-camel text-2xl">{species}</p>
          <h1 className="font-serif-display text-5xl md:text-6xl text-ink mt-1 leading-none">
            {toy.name}
          </h1>

          <p className="font-body text-3xl text-terracotta mt-6 font-light">
            {t.common.currency}{toy.price} <span className="text-base text-camel ml-1">{t.common.currencyCode}</span>
          </p>

          <p className="font-body text-base text-muted-foreground mt-6 leading-relaxed">
            {description}
          </p>

          <dl className="mt-10 space-y-5 border-t border-border pt-8">
            <PedigreeRow Icon={Calendar} label={t.common.born} value={toy.birth_date} />
            <PedigreeRow Icon={Clock} label={t.common.creationTime} value={`${toy.creation_hours}h`} />
            <PedigreeRow Icon={Palette} label={t.common.colors} value={toy.colors.join(", ")} />
            <PedigreeRow Icon={Sparkle} label={t.common.personality} value={personality} />
          </dl>

          {toy.available ? (
            <button
              onClick={() => navigate(`/adopt/${toy.id}`)}
              data-testid="adopt-cta"
              className="mt-10 w-full inline-flex items-center justify-center gap-3 bg-terracotta text-background px-8 py-4 rounded-full font-body text-sm tracking-wider uppercase hover:shadow-warm transition-all"
            >
              <Heart size={16} strokeWidth={1.5} fill="currentColor" />
              {t.common.adoptMe} — {t.common.currency}{toy.price} {t.common.currencyCode}
            </button>
          ) : (
            <div
              data-testid="adopted-banner"
              className="mt-10 text-center py-4 rounded-full bg-ink text-background font-body text-sm uppercase tracking-widest"
            >
              {t.common.adopted}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PedigreeRow = ({ Icon, label, value }) => (
  <div className="flex items-start gap-4">
    <Icon size={18} strokeWidth={1.3} className="text-camel mt-0.5" />
    <div>
      <dt className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{label}</dt>
      <dd className="font-body text-base text-ink mt-0.5">{value || "—"}</dd>
    </div>
  </div>
);

export default ToyDetail;
